import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export interface InterceptModalProps {
  isOpen: boolean;
  onClose: () => void;
  actionName?: string | undefined;
}

export function InterceptModal({ isOpen, onClose, actionName }: InterceptModalProps) {
  const navigate = useNavigate();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center px-4 sm:px-6">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-xs transition-opacity duration-200"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal Card */}
      <div className="relative z-10 bg-card border border-line rounded-xl3 shadow-cardhover max-w-md w-full p-6 sm:p-8 transform transition-all duration-300 animate-log-slide">
        {/* Icon */}
        <div className="w-12 h-12 rounded-xl bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center mb-5 text-blue">
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>

        <h3 className="text-xl font-bold text-ink mb-2">
          Create an Account to Continue
        </h3>

        <p className="text-sm text-sub leading-relaxed mb-6">
          {actionName ? (
            <>
              Write operations like <span className="font-semibold text-ink">"{actionName}"</span> are disabled in the shared demo workspace.
            </>
          ) : (
            'Write operations are disabled in the shared demo workspace.'
          )}{' '}
          Sign up for a free Consentry account to manage applications, publish notices, and record real consent.
        </p>

        {/* CTAs */}
        <div className="space-y-3">
          <button
            type="button"
            onClick={() => {
              onClose();
              navigate('/register');
            }}
            className="btn-p w-full justify-center text-sm py-3 font-semibold cursor-pointer"
          >
            Create Free Account
          </button>
          
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => {
                onClose();
                navigate('/login');
              }}
              className="btn-g flex-1 justify-center text-xs py-2.5 text-sub hover:text-ink cursor-pointer"
            >
              Sign In
            </button>
            <button
              type="button"
              onClick={onClose}
              className="btn-g flex-1 justify-center text-xs py-2.5 text-muted hover:text-ink cursor-pointer"
            >
              Back to Demo
            </button>
          </div>
        </div>

        <div className="mt-5 pt-4 border-t border-line text-center">
          <span className="mono text-[11px] text-muted">
            Free tier available · India DPDP Act 2023 compliant · No credit card required
          </span>
        </div>
      </div>
    </div>
  );
}
